import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FloatingPopupTabsComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-tabs/floating-popup-tabs.component';
import { FloatingPopupBaseContentComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-base-content/floating-popup-base-content.component';
import { FLOATING_POPUP_DATA } from '@app/main/main-content/floating-popup-manager/providers/floating-popup-data.token';
import { FloatingPopupTabsService } from '@app/main/main-content/floating-popup-manager/services/floating-popup-tabs.service';
import { FpCorrelationTabs } from '../../enums/fp-correlation-tabs.enum';
import { ButtonModule } from 'primeng/button';
import { SvgIconComponent } from 'angular-svg-icon';
import { TranslatePipe } from '@ngx-translate/core';
import { Tooltip } from 'primeng/tooltip';
import { ScreenResolutionService } from '@app/core/services/screen-resolution/screen-resolution.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { FpCorrelationManagerStore } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/services/fp-correlation-manager/fp-correlation-manager.store';
import { FpCorrelationManagerActionType } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/services/fp-correlation-manager/fp-correlation-manager.actions';
import { IFpCorrelationData } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/interfaces/fp-correlation-data';
import { SpinnerComponent } from '@app/shared/components/spinner/spinner.component';
import { FloatingPopupBaseErrorMessageComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-base-error-message/floating-popup-base-error-message.component';

@Component({
  selector: 'gc-fp-correlation-content',
  standalone: true,
  imports: [
    CommonModule,
    FloatingPopupTabsComponent,
    ButtonModule,
    SvgIconComponent,
    TranslatePipe,
    Tooltip,
    SpinnerComponent,
    FloatingPopupBaseErrorMessageComponent,
  ],
  templateUrl: './fp-correlation-content.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FpCorrelationContentComponent extends FloatingPopupBaseContentComponent {
  public readonly data = inject<IFpCorrelationData>(FLOATING_POPUP_DATA);
  public readonly scrRes = inject(ScreenResolutionService);
  private readonly floatingPopupTabs = inject(FloatingPopupTabsService);
  private readonly floatingPopupCorrelationManagerStore = inject(FpCorrelationManagerStore);

  public selectedTab = computed(() => this.floatingPopupTabs.getActive(this.popupId())());

  public tabs = computed(() => this.service.prepareFormControlConfigForPopupTab(this.popupId()), {
    equal: (a, b) => a?.id === b?.id,
  });

  public activeTabConnectService = toSignal(this.floatingPopupCorrelationManagerStore.activeTabConnectService$, {
    initialValue: null,
  });

  public loading = toSignal(this.floatingPopupCorrelationManagerStore.loading$, {
    initialValue: false,
  });

  public selectedData = toSignal(this.floatingPopupCorrelationManagerStore.selectedData$, {
    initialValue: null,
  });

  public initialized = toSignal(this.floatingPopupCorrelationManagerStore.initialized$, {
    initialValue: false,
  });

  public activeTabLoading = toSignal(this.floatingPopupCorrelationManagerStore.activeTabLoading$, {
    initialValue: false,
  });

  public activeTabIsChartFullscreen = toSignal(this.floatingPopupCorrelationManagerStore.activeTabIsChartFullscreen$, {
    initialValue: false,
  });

  constructor() {
    super();

    const effectRef = effect(() => {
      const popupId = this.popupId();

      if (popupId) {
        this.initPopupStoreManager(popupId);

        effectRef.destroy();
      }
    });
  }

  private initPopupStoreManager(popupId: string) {
    this.floatingPopupCorrelationManagerStore.dispatch({
      type: FpCorrelationManagerActionType.Init,
      popupId: popupId,
      selectedData: this.data,
      activeTab: this.floatingPopupTabs.getActive(popupId)() as FpCorrelationTabs,
    });
  }
}
